import React, { useMemo, useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// === Styled Components (matching CompanyAnalysis style) ===
const ChartWrapper = styled.div`
  position: relative; /* important for the info icon */
  background-color: #f9f9f9;
  width: 100%;
  height: 55vh;           /* same height pattern */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 10px;
  right: 15px;
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover { color: #000; }
  svg { width: 18px; height: 18px; }
`;

const FiltersWrapper = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 14px;
  justify-content: center;

  select {
    padding: 6px 10px;
    font-size: 14px;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
`;

const ChartTitle = styled.h3`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  text-align: center;
  color: #666;
  margin: 0 0 8px 0;
`;

// === Helpers / constants ===
const monthNamesPL = [
  "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
  "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
];

const GREEN  = "#2e7d32";            // current Nowi
const ORANGE = "#fb8c00";            // current Stali
const LGREEN = "rgba(46, 125, 50, 0.35)"; // previous Nowi (lighter)
const LORANGE= "rgba(251, 140, 0, 0.40)"; // previous Stali (lighter)

function buildCustomerMaps(data) {
  const customerFirstPurchase = {};
  const monthlyCustomerMap = {}; // key: YYYY-MM -> Set(customerId)

  data.forEach((item) => {
    const customerId = item.NrKontrahenta;
    const rawDate = item.DataWystawienia;
    const date = new Date(rawDate);
    if (!customerId || isNaN(date)) return;

    if (!customerFirstPurchase[customerId] || date < customerFirstPurchase[customerId]) {
      customerFirstPurchase[customerId] = date;
    }

    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    if (!monthlyCustomerMap[key]) monthlyCustomerMap[key] = new Set();
    monthlyCustomerMap[key].add(customerId);
  });

  return { customerFirstPurchase, monthlyCustomerMap };
}

function monthlyCountsForYear(year, customerFirstPurchase, monthlyCustomerMap) {
  // Always return 12 rows (Jan..Dec), even if zeros
  const rows = Array.from({ length: 12 }, () => ({ Nowi: 0, Stali: 0 }));

  for (let m = 0; m < 12; m++) {
    const monthKey = `${year}-${String(m + 1).padStart(2, "0")}`;
    const customers = monthlyCustomerMap[monthKey];
    if (!customers) continue;

    customers.forEach((cid) => {
      const first = customerFirstPurchase[cid];
      const firstKey = `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}`;
      if (firstKey === monthKey) rows[m].Nowi += 1;
      else rows[m].Stali += 1;
    });
  }

  return rows;
}

const NewVsRegularCustomers = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(null);

  const availableYears = useMemo(() => {
    const years = new Set();
    data.forEach((item) => {
      const date = new Date(item.DataWystawienia);
      if (!isNaN(date)) years.add(date.getFullYear());
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [data]);

  useEffect(() => {
    if (availableYears.length && selectedYear === null) {
      setSelectedYear(availableYears[availableYears.length - 1]); // latest
    }
  }, [availableYears, selectedYear]);

  // Precompute customer maps once
  const { customerFirstPurchase, monthlyCustomerMap } = useMemo(
    () => buildCustomerMaps(data || []),
    [data]
  );

  // Build series for selected year and previous year
  const chartRows = useMemo(() => {
    if (!selectedYear) return [];

    const curr = monthlyCountsForYear(selectedYear, customerFirstPurchase, monthlyCustomerMap);
    const prev = monthlyCountsForYear(selectedYear - 1, customerFirstPurchase, monthlyCustomerMap);

    return curr.map((row, idx) => ({
      month: monthNamesPL[idx],
      Nowi: row.Nowi,
      Stali: row.Stali,
      NowiPrev: prev[idx]?.Nowi || 0,
      StaliPrev: prev[idx]?.Stali || 0,
    }));
  }, [selectedYear, customerFirstPurchase, monthlyCustomerMap]);

  if (!availableYears.length) return <p>Ładowanie danych...</p>;

  return (
    <ChartWrapper>
      <InfoIconWrapper data-tooltip-id="new-regular-info">
        <Info />
      </InfoIconWrapper>
      <InfoTooltip
        id="new-regular-info"
        place="left"
        content={
          <>
            <strong>Nowi vs. Stali Klienci</strong><br />
            Porównanie {selectedYear} (zielone/pomarańczowe) vs {selectedYear - 1} (półprzezroczyste).
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label htmlFor="yearSelect">Rok:&nbsp;</label>
          <select
            id="yearSelect"
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </FiltersWrapper>

      <ChartTitle>
        Nowi vs. Stali Klienci – {selectedYear} vs {selectedYear - 1}
      </ChartTitle>

      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartRows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="month" />
          <YAxis allowDecimals={false} />
          <ReTooltip />
          <Legend />

          {/* Current year – Nowi (green) */}
          <Bar dataKey="Nowi" name={`Nowi (${selectedYear})`} fill={GREEN} />

          {/* Current year – Stali (orange) */}
          <Bar dataKey="Stali" name={`Stali (${selectedYear})`} fill={ORANGE} />

          {/* Previous year – Nowi (lighter green) */}
          <Bar dataKey="NowiPrev" name={`Nowi (${selectedYear - 1})`} fill={LGREEN} />

          {/* Previous year – Stali (lighter orange) */}
          <Bar dataKey="StaliPrev" name={`Stali (${selectedYear - 1})`} fill={LORANGE} />
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
};

export default NewVsRegularCustomers;
